import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Shield } from "lucide-react";

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen pt-20" data-testid="privacy-policy-page">
      <section className="py-16 md:py-24 bg-gradient-to-b from-casino-black to-casino-dark">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="w-8 h-8 text-gold" />
            <h1 className="font-heading text-4xl md:text-5xl text-white">Privacy Policy</h1>
          </div>
          <p className="text-white/50 text-sm">Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
        </div>
      </section>

      <section className="py-16 bg-casino-black">
        <div className="max-w-4xl mx-auto px-4 space-y-10">
          {/* Introduction */}
          <div className="prose-section">
            <h2 className="font-heading text-2xl text-white mb-4">Introduction</h2>
            <p className="text-white/70 leading-relaxed">
              San Marcos Social Club ("we," "our," or "us") is committed to protecting the privacy of our visitors and customers. This Privacy Policy describes how we collect, use, and share information when you visit our website, use our services, or interact with us. By using our website, you agree to the collection and use of information in accordance with this policy.
            </p>
          </div>

          {/* Information We Collect */}
          <div className="prose-section">
            <h2 className="font-heading text-2xl text-white mb-4">Information We Collect</h2>
            <p className="text-white/70 leading-relaxed mb-4">
              We collect information you provide directly to us, including:
            </p>
            <ul className="space-y-2 text-white/70">
              <li className="flex items-start gap-2">
                <span className="text-gold mt-1.5 text-xs">&#9679;</span>
                <span><strong className="text-white">Contact Information:</strong> First name, last name, email address, and phone number when you submit our contact form or request information.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gold mt-1.5 text-xs">&#9679;</span>
                <span><strong className="text-white">Communications:</strong> Messages and other content you provide when contacting us.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gold mt-1.5 text-xs">&#9679;</span>
                <span><strong className="text-white">SMS Consent:</strong> Your opt-in preference for receiving text messages from us.</span>
              </li>
            </ul>
          </div>

          {/* How We Use Your Information */}
          <div className="prose-section">
            <h2 className="font-heading text-2xl text-white mb-4">How We Use Your Information</h2>
            <p className="text-white/70 leading-relaxed mb-4">
              We use the information we collect to:
            </p>
            <ul className="space-y-2 text-white/70">
              <li className="flex items-start gap-2">
                <span className="text-gold mt-1.5 text-xs">&#9679;</span>
                <span>Respond to your inquiries and provide customer service</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gold mt-1.5 text-xs">&#9679;</span>
                <span>Send appointment confirmations and reminders</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gold mt-1.5 text-xs">&#9679;</span>
                <span>Provide service updates and follow-up communications</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gold mt-1.5 text-xs">&#9679;</span>
                <span>Improve our website and services</span>
              </li>
            </ul>
          </div>

          {/* SMS / Text Messaging Section */}
          <div className="prose-section glass-card p-8 border-gold/20" data-testid="sms-privacy-section">
            <h2 className="font-heading text-2xl text-gold mb-4">SMS / Text Messaging</h2>
            <p className="text-white/70 leading-relaxed mb-4">
              If you opt in to receive text messages from San Marcos Social Club, the following terms apply:
            </p>
            <p className="text-white/70 leading-relaxed mb-4">
              <strong className="text-white">Phone numbers are collected for:</strong>
            </p>
            <ul className="space-y-2 text-white/70 mb-6">
              <li className="flex items-start gap-2">
                <span className="text-gold mt-1.5 text-xs">&#9679;</span>
                <span>Appointment confirmations</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gold mt-1.5 text-xs">&#9679;</span>
                <span>Appointment reminders</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gold mt-1.5 text-xs">&#9679;</span>
                <span>Service updates</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gold mt-1.5 text-xs">&#9679;</span>
                <span>Follow-up communications</span>
              </li>
            </ul>
            <ul className="space-y-3 text-white/70">
              <li className="flex items-start gap-2">
                <span className="text-gold mt-1.5 text-xs">&#9679;</span>
                <span>Message frequency may vary.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gold mt-1.5 text-xs">&#9679;</span>
                <span>Message and data rates may apply.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gold mt-1.5 text-xs">&#9679;</span>
                <span>You can reply <strong className="text-white">STOP</strong> to opt out of text messages at any time.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gold mt-1.5 text-xs">&#9679;</span>
                <span>You can reply <strong className="text-white">HELP</strong> for assistance.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gold mt-1.5 text-xs">&#9679;</span>
                <span><strong className="text-white">SMS consent is not a condition of purchase.</strong> You are not required to consent to receiving text messages in order to use our services or make a purchase.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gold mt-1.5 text-xs">&#9679;</span>
                <span><strong className="text-white">Phone numbers and SMS consent are never sold or shared with third parties for marketing purposes.</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gold mt-1.5 text-xs">&#9679;</span>
                <span>Phone numbers may only be shared with service providers required to deliver messages (SMS platforms/carriers).</span>
              </li>
            </ul>
          </div>

          {/* Information Sharing */}
          <div className="prose-section">
            <h2 className="font-heading text-2xl text-white mb-4">Information Sharing</h2>
            <p className="text-white/70 leading-relaxed">
              We do not sell, rent, or share your personal information with third parties for their marketing purposes. We may share your information only in the following circumstances:
            </p>
            <ul className="space-y-2 text-white/70 mt-4">
              <li className="flex items-start gap-2">
                <span className="text-gold mt-1.5 text-xs">&#9679;</span>
                <span><strong className="text-white">Service Providers:</strong> We may share information with third-party service providers who assist us in operating our website, conducting our business, or servicing you (e.g., SMS delivery platforms and carriers), so long as those parties agree to keep this information confidential.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gold mt-1.5 text-xs">&#9679;</span>
                <span><strong className="text-white">Legal Requirements:</strong> We may disclose your information where required by law or to protect our rights.</span>
              </li>
            </ul>
          </div>

          {/* Data Security */}
          <div className="prose-section">
            <h2 className="font-heading text-2xl text-white mb-4">Data Security</h2>
            <p className="text-white/70 leading-relaxed">
              We implement reasonable security measures to protect the personal information we collect and maintain. However, no method of transmission over the Internet or electronic storage is 100% secure.
            </p>
          </div>

          {/* Your Rights */}
          <div className="prose-section">
            <h2 className="font-heading text-2xl text-white mb-4">Your Rights</h2>
            <p className="text-white/70 leading-relaxed">
              You have the right to request access to, correction of, or deletion of your personal information. To exercise these rights, contact us using the information provided below.
            </p>
          </div>

          {/* Contact Us */}
          <div className="prose-section">
            <h2 className="font-heading text-2xl text-white mb-4">Contact Us</h2>
            <p className="text-white/70 leading-relaxed">
              If you have questions about this Privacy Policy, please contact us at:
            </p>
            <div className="mt-4 text-white/70">
              <p className="font-medium text-white">San Marcos Social Club</p>
              <span className="text-white/40 italic">
                Address, phone &amp; email coming soon
              </span>
            </div>
          </div>

          <div className="pt-8 border-t border-white/10">
            <Link to="/contact" className="text-gold hover:text-gold-light transition-colors text-sm">
              &larr; Back to Contact
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
